import { api } from './client';

/**
 * Where a day's reading was taken. Mirrors `WeatherPlace` on the server.
 *
 * `from: 'trip'` means no stop of that day had coordinates and the trip's own
 * destination was used — the centre of a city or of a country. The UI says so
 * rather than passing it off as a stop.
 */
export interface WeatherPlace {
  name: string;
  from: 'stop' | 'trip';
}

/** Mirrors `TripDayWeather` in `server/src/services/weather/shared.ts`. */
export interface DayWeather {
  date: string;                       // YYYY-MM-DD
  source: 'forecast' | 'climate';
  tempMax: number | null;
  tempMin: number | null;
  precipMm: number | null;
  /** Raw met.no symbol_code — turned into words + icon by `utils/weatherSymbol`. */
  symbol: string | null;
  /** The stop the numbers are about. `null` when the trip has no name for it. */
  place: WeatherPlace | null;
}

/** Mirrors `HourGranularity` in `server/src/services/weather/shared.ts`. */
export type HourGranularity = 'hourly' | 'sixHour';

/** Mirrors `HourWeather` on the server. */
export interface HourWeather {
  time: string;                       // ISO instant, UTC
  temp: number | null;
  symbol: string | null;
  precipMm: number | null;
  windSpeed: number | null;           // m/s, as met.no serves it
  windFromDirection: number | null;   // degrees the wind comes FROM
}

/**
 * Mirrors `DayHours` on the server.
 *
 * Only days the forecast reaches have one. A day past its horizon is absent
 * here — never present with invented hours — because the only source that far
 * out is a monthly average (ADR-0006).
 */
export interface DayHours {
  date: string;
  granularity: HourGranularity;
  hours: HourWeather[];
}

export const weatherApi = {
  get: (tripId: string) =>
    api.get<{ days: DayWeather[] }>(`/trips/${tripId}/weather`).then(r => r.data.days),

  hourly: (tripId: string) =>
    api.get<{ days: DayHours[] }>(`/trips/${tripId}/weather/hourly`).then(r => r.data.days),
};
